export interface MatchProfile {
  id: string;
  name: string | null;
  gender: string | null;
  age: number | null;
  birth_date: string | null;
  height: number | null;
  weight: number | null;
  education: string | null;
  marital_status: string | null;
  children_status: string | null;
  residence: string | null;
  hometown: string | null;
  occupation: string | null;
  income: string | null;
  house_status: string | null;
  car_status: string | null;
  phone: string | null;
  wechat: string | null;
  hobbies: string | null;
  self_intro: string | null;
  matchmaker_note: string | null;
  partner_age_min: number | null;
  partner_age_max: number | null;
  partner_requirements: string | null;
  photo_url: string | null;
  status: string | null;
  created_by_name: string | null;
  created_at: string;
}

export const GENDER_OPTIONS = ["男"];
export const MARITAL_OPTIONS = ["未婚", "离异", "丧偶"];
export const CHILDREN_OPTIONS = ["无子女", "子女未成年同住", "子女已独立", "子女已成家"];
export const HOUSE_OPTIONS = ["有房无贷款", "有房有贷款", "与子女同住", "租房居住", "暂未购房"];
export const CAR_OPTIONS = ["有车", "无车"];
export const INCOME_OPTIONS = [
  "10万以内",
  "10-20万",
  "30-50万",
  "50-100万",
];

export interface StatusMeta {
  value: string;
  label: string;
  badge: string;
  dot: string;
}

export const STATUS_OPTIONS: StatusMeta[] = [
  { value: "pending", label: "待处理", badge: "bg-accent-100 text-accent-800", dot: "bg-accent-500" },
  { value: "contacted", label: "已联系", badge: "bg-secondary-100 text-secondary-800", dot: "bg-secondary-500" },
  { value: "matched", label: "已匹配", badge: "bg-primary-100 text-primary-700", dot: "bg-primary-500" },
  { value: "closed", label: "已下线", badge: "bg-background-200 text-foreground-600", dot: "bg-foreground-400" },
];

export function getStatusMeta(status?: string | null): StatusMeta {
  return STATUS_OPTIONS.find((item) => item.value === status) || STATUS_OPTIONS[0];
}

export interface DetailField {
  key: keyof MatchProfile;
  label: string;
  suffix?: string;
  full?: boolean;
}

export const OVERVIEW_FIELDS: DetailField[] = [
  { key: "name", label: "姓名" },
  { key: "gender", label: "性别" },
  { key: "age", label: "年龄", suffix: " 岁" },
  { key: "birth_date", label: "出生年月日" },
  { key: "height", label: "身高", suffix: " cm" },
  { key: "weight", label: "体重", suffix: " kg" },
  { key: "education", label: "学历" },
  { key: "marital_status", label: "婚姻状况" },
  { key: "children_status", label: "子女情况" },
  { key: "residence", label: "现居城市" },
  { key: "hometown", label: "籍贯" },
  { key: "occupation", label: "职业" },
  { key: "income", label: "年收入" },
  { key: "house_status", label: "住房情况" },
  { key: "car_status", label: "车辆情况" },
  { key: "hobbies", label: "兴趣爱好", full: true },
  { key: "self_intro", label: "自我介绍", full: true },
  { key: "partner_age_min", label: "期望对方年龄（起）", suffix: " 岁" },
  { key: "partner_age_max", label: "期望对方年龄（止）", suffix: " 岁" },
  { key: "partner_requirements", label: "择偶要求", full: true },
  { key: "created_by_name", label: "上传人" },
];

export const MATCH_NOTE_FIELD: DetailField = {
  key: "matchmaker_note",
  label: "匹配记录",
  full: true,
};

export const CONTACT_FIELDS: DetailField[] = [
  { key: "phone", label: "手机号" },
  { key: "wechat", label: "微信号" },
];

export function formatDateTime(value?: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
}

export function displayValue(value: unknown, suffix?: string): string {
  if (value === null || value === undefined || value === "") return "—";
  return `${value}${suffix || ""}`;
}

/**
 * 解析照片字段。
 * 兼容两种存法：老数据（单张 URL 纯文本）与新数据（多张的 JSON 数组字符串）。
 */
export function parsePhotoUrls(value: string | null | undefined): string[] {
  if (!value) return [];
  const trimmed = value.trim();
  if (!trimmed) return [];
  if (trimmed.startsWith("[")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.filter(
          (item) => typeof item === "string" && item.trim() !== "",
        );
      }
    } catch {
      // 不是合法 JSON，则按单张 URL 处理
    }
  }
  return [trimmed];
}

/**
 * 把照片数组序列化后存回 photo_url 字段。
 * 单张时存纯 URL（保持老数据兼容），多张时存 JSON 数组字符串。
 */
export function serializePhotoUrls(urls: string[]): string {
  const clean = urls.map((url) => url.trim()).filter(Boolean);
  if (clean.length === 0) return "";
  if (clean.length === 1) return clean[0];
  return JSON.stringify(clean);
}

/** 取第一张作为封面（列表头像、详情头图用）。 */
export function getCoverPhoto(value: string | null | undefined): string {
  return parsePhotoUrls(value)[0] || "";
}

export type StaffRoleKey = "admin" | "sales" | "matchmaker";

export interface RoleMeta {
  label: string;
  badge: string;
  icon: string;
  desc: string;
}

export const ROLE_META: Record<StaffRoleKey, RoleMeta> = {
  admin: {
    label: "管理员",
    badge: "bg-primary-100 text-primary-700",
    icon: "ri-shield-star-line",
    desc: "拥有全部权限：查看完整手机号、编辑与导入资料、删除会员、管理员工账号",
  },
  matchmaker: {
    label: "红娘",
    badge: "bg-accent-100 text-accent-700",
    icon: "ri-heart-2-line",
    desc: "可查看完整手机号、编辑资料、上传照片、新增与导入会员；不能删除会员或管理员工账号",
  },
  sales: {
    label: "销售",
    badge: "bg-secondary-100 text-secondary-800",
    icon: "ri-user-star-line",
    desc: "可查看会员资料，手机号自动脱敏显示",
  },
};

export interface StaffMember {
  user_id: string;
  username: string | null;
  email: string | null;
  display_name: string | null;
  role: StaffRoleKey;
  created_at: string;
}